import {
  createFile,
  DataStream,
  Endianness,
  MP4BoxBuffer,
  type ISOFile,
  type Movie,
  type Sample,
  type Track,
} from 'mp4box';
import { Clip, clipTimeSec, notify, state } from './state.ts';

/**
 * Scrub frame cache (docs/SCRUBBING.md option A).
 *
 * Long-GOP sources (ComfyUI outputs are often a single keyframe per file)
 * make <video> seeks decode the whole GOP tail per scrub step. Instead we
 * demux the mp4 once with mp4box.js, then on demand decode the GOP around
 * the playhead with WebCodecs VideoDecoder and keep a sliding window of
 * ImageBitmaps. The compositor prefers a cached bitmap while paused, so
 * rocking back and forth over a region is instant after one decode pass.
 * Playback and export keep using the <video> element path.
 */

const MAX_CACHE_BYTES = 512 * 1024 * 1024; // ImageBitmaps are GPU-backed; keep a hard lid
const MAX_FILE_BYTES = 600 * 1024 * 1024; // demux keeps sample data in memory ≈ file size
const WINDOW_BEHIND = 18; // bitmaps kept behind the scrub target (per fill)
const WINDOW_AHEAD = 30; // …and ahead of it
const MAX_FAILURES = 3;

interface FrameEntry {
  bmp: ImageBitmap;
  bytes: number;
  lastUse: number;
}

interface ClipCache {
  config: VideoDecoderConfig;
  /** Samples in decode order, encoded bytes retained (≈ file size). */
  samples: Sample[];
  /** presentation index → decode index */
  presToDecode: number[];
  /** presentation times in seconds, normalized to start at 0, sorted */
  presTimes: number[];
  /** normalized chunk timestamp (µs) → presentation index */
  tsToPres: Map<number, number>;
  ctsBase: number;
  frames: Map<number, FrameEntry>;
  decoder: VideoDecoder | null;
  inFlightTarget: number; // presentation index a decode is running for, -1 idle
  gen: number;
  failures: number;
}

const caches = new Map<string, ClipCache>();
let totalBytes = 0;
let useTick = 0;

// ---------- build ----------

export async function initFrameCache(clip: Clip): Promise<void> {
  if (clip.kind !== 'video' || clip.error) return;
  if (typeof VideoDecoder === 'undefined') return;
  const isMp4 =
    /\.(mp4|mov|m4v)$/i.test(clip.name) ||
    ['video/mp4', 'video/quicktime'].includes(clip.file.type);
  if (!isMp4 || clip.file.size > MAX_FILE_BYTES) return;

  clip.scrubCache = 'building';
  notify();
  try {
    const cache = await buildCache(clip);
    // The clip may have been removed while we were parsing.
    if (!state.clips.some((c) => c.id === clip.id)) return;
    caches.set(clip.id, cache);
    clip.scrubCache = 'ready';
  } catch {
    clip.scrubCache = 'off';
  }
  notify();
}

async function buildCache(clip: Clip): Promise<ClipCache> {
  const buf = await clip.file.arrayBuffer();
  const file = createFile();
  const samples: Sample[] = [];
  let track: Track | undefined;

  const parsed = new Promise<void>((resolve, reject) => {
    file.onError = (e: unknown) => reject(new Error(String(e)));
    file.onReady = (info: Movie) => {
      track = info.videoTracks[0];
      if (!track) {
        reject(new Error('no video track'));
        return;
      }
      file.setExtractionOptions(track.id, undefined, { nbSamples: track.nb_samples });
      file.start();
    };
    file.onSamples = (_id, _user, batch) => {
      samples.push(...batch);
      if (track && samples.length >= track.nb_samples) resolve();
    };
  });

  file.appendBuffer(MP4BoxBuffer.fromArrayBuffer(buf, 0), true);
  file.flush();
  // mp4box works synchronously on a fully-appended buffer; the timeout is a
  // guard for truncated files — proceed with whatever was extracted.
  await Promise.race([parsed, new Promise<void>((r) => setTimeout(r, 5000))]);
  file.stop();

  if (!track || samples.length === 0) throw new Error('demux produced no samples');

  const config: VideoDecoderConfig = {
    codec: track.codec,
    codedWidth: track.video?.width || clip.width,
    codedHeight: track.video?.height || clip.height,
    description: extractDescription(file, track.id),
    // Scrubbing wants lowest latency; hardware where possible.
    optimizeForLatency: true,
  };
  const support = await VideoDecoder.isConfigSupported(config);
  if (!support.supported) throw new Error(`decoder unsupported: ${track.codec}`);

  const order = samples.map((_, i) => i).sort((a, b) => samples[a].cts - samples[b].cts);
  const ctsBase = samples[order[0]].cts;
  const presTimes = order.map((di) => (samples[di].cts - ctsBase) / samples[di].timescale);
  const tsToPres = new Map<number, number>();
  order.forEach((di, pi) => tsToPres.set(chunkTimestamp(samples[di], ctsBase), pi));

  return {
    config,
    samples,
    presToDecode: order,
    presTimes,
    tsToPres,
    ctsBase,
    frames: new Map(),
    decoder: null,
    inFlightTarget: -1,
    gen: 0,
    failures: 0,
  };
}

interface DescriptionBox {
  write(stream: DataStream): void;
}

/** Serialize the codec-private box (avcC/hvcC/…) — VideoDecoder's `description`. */
function extractDescription(file: ISOFile, trackId: number): Uint8Array | undefined {
  const trak = file.getTrackById(trackId);
  const entries =
    (trak as unknown as { mdia?: { minf?: { stbl?: { stsd?: { entries?: unknown[] } } } } }).mdia
      ?.minf?.stbl?.stsd?.entries ?? [];
  for (const entry of entries) {
    const e = entry as Record<string, DescriptionBox | undefined>;
    const box = e.avcC ?? e.hvcC ?? e.vpcC ?? e.av1C;
    if (box) {
      const stream = new DataStream(undefined, 0, Endianness.BIG_ENDIAN);
      box.write(stream);
      return new Uint8Array(stream.buffer as ArrayBuffer, 8); // strip box size+type header
    }
  }
  return undefined;
}

function chunkTimestamp(s: Sample, ctsBase: number): number {
  return Math.round(((s.cts - ctsBase) * 1_000_000) / s.timescale);
}

// ---------- lookup (compositor hot path) ----------

/**
 * Return the exact decoded frame for a clip-local time if cached; on a miss,
 * kick an async GOP decode and return null (caller falls back to the <video>).
 */
export function getScrubFrame(clip: Clip, tSec: number): ImageBitmap | null {
  const cache = caches.get(clip.id);
  if (!cache) return null;
  const pIdx = presIndexFor(cache, tSec);
  const hit = cache.frames.get(pIdx);
  if (hit) {
    hit.lastUse = ++useTick;
    return hit.bmp;
  }
  requestFill(cache, pIdx);
  return null;
}

/** Called from the scrub path to warm the cache for the current frame. */
export function prefetch(clip: Clip): void {
  if (state.playing || state.exporting) return;
  const cache = caches.get(clip.id);
  if (!cache) return;
  const pIdx = presIndexFor(cache, clipTimeSec(clip, state.frame));
  if (!cache.frames.has(pIdx)) requestFill(cache, pIdx);
}

function presIndexFor(cache: ClipCache, tSec: number): number {
  const times = cache.presTimes;
  let lo = 0;
  let hi = times.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (times[mid] <= tSec + 1e-6) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

// ---------- decode ----------

function requestFill(cache: ClipCache, targetPres: number): void {
  if (cache.failures >= MAX_FAILURES) return;
  // A decode close to this target is already running — let it finish.
  if (cache.inFlightTarget >= 0 && Math.abs(cache.inFlightTarget - targetPres) <= 8) return;
  void decodeWindow(cache, targetPres);
}

async function decodeWindow(cache: ClipCache, targetPres: number): Promise<void> {
  const gen = ++cache.gen;
  cache.inFlightTarget = targetPres;
  if (cache.decoder && cache.decoder.state !== 'closed') {
    try {
      cache.decoder.close();
    } catch {
      /* already closing */
    }
  }

  const { samples } = cache;
  const targetDec = cache.presToDecode[targetPres];
  let gopStart = targetDec;
  while (gopStart > 0 && !samples[gopStart].is_sync) gopStart--;
  let gopEnd = targetDec + 1;
  while (gopEnd < samples.length && !samples[gopEnd].is_sync) gopEnd++;
  gopEnd--; // last sample of this GOP

  const keepFrom = Math.max(0, targetPres - WINDOW_BEHIND);
  const keepTo = targetPres + WINDOW_AHEAD;
  const pending: Promise<void>[] = [];

  const decoder = new VideoDecoder({
    output: (frame) => {
      if (gen !== cache.gen) {
        frame.close();
        return;
      }
      const pIdx = cache.tsToPres.get(frame.timestamp);
      if (pIdx === undefined || pIdx < keepFrom || pIdx > keepTo || cache.frames.has(pIdx)) {
        frame.close();
        return;
      }
      pending.push(
        createImageBitmap(frame)
          .then((bmp) => {
            frame.close();
            if (gen !== cache.gen) {
              bmp.close();
              return;
            }
            storeFrame(cache, pIdx, bmp);
          })
          .catch(() => frame.close())
      );
    },
    error: () => {
      cache.failures++;
    },
  });
  cache.decoder = decoder;
  decoder.configure(cache.config);

  try {
    for (let i = gopStart; i <= gopEnd; i++) {
      const s = samples[i];
      if (!s.data) continue;
      decoder.decode(
        new EncodedVideoChunk({
          type: s.is_sync ? 'key' : 'delta',
          timestamp: chunkTimestamp(s, cache.ctsBase),
          duration: Math.round((s.duration * 1_000_000) / s.timescale),
          data: s.data,
        })
      );
    }
    await decoder.flush();
    await Promise.all(pending);
    cache.failures = 0;
  } catch {
    if (gen === cache.gen) cache.failures++;
  } finally {
    if (gen === cache.gen) {
      cache.inFlightTarget = -1;
      if (decoder.state !== 'closed') decoder.close();
    }
  }
}

function storeFrame(cache: ClipCache, pIdx: number, bmp: ImageBitmap): void {
  const bytes = bmp.width * bmp.height * 4;
  cache.frames.set(pIdx, { bmp, bytes, lastUse: ++useTick });
  totalBytes += bytes;
  evictIfNeeded();
}

function evictIfNeeded(): void {
  while (totalBytes > MAX_CACHE_BYTES) {
    let oldestCache: ClipCache | null = null;
    let oldestIdx = -1;
    let oldestUse = Infinity;
    for (const cache of caches.values()) {
      for (const [idx, entry] of cache.frames) {
        if (entry.lastUse < oldestUse) {
          oldestUse = entry.lastUse;
          oldestCache = cache;
          oldestIdx = idx;
        }
      }
    }
    if (!oldestCache) return;
    const entry = oldestCache.frames.get(oldestIdx)!;
    entry.bmp.close();
    totalBytes -= entry.bytes;
    oldestCache.frames.delete(oldestIdx);
  }
}

// ---------- teardown ----------

export function disposeFrameCache(clipId: string): void {
  const cache = caches.get(clipId);
  if (!cache) return;
  cache.gen++;
  if (cache.decoder && cache.decoder.state !== 'closed') {
    try {
      cache.decoder.close();
    } catch {
      /* already closing */
    }
  }
  for (const entry of cache.frames.values()) {
    entry.bmp.close();
    totalBytes -= entry.bytes;
  }
  cache.frames.clear();
  cache.samples.length = 0;
  caches.delete(clipId);
}
